import React, { useState } from 'react';
import api from '../services/api';

const ProperCSVUploader = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('csvFile', file);

      const data = await api.request('/proper-csv/upload', {
        method: 'POST',
        body: formData
      });

      if (data.success) {
        setResult(data);
        setFile(null);
        document.getElementById('properCsvFileInput').value = '';
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Proper CSV Upload Error:', err);
      setError('Upload failed: ' + (err.message || err.toString()));
    } finally {
      setUploading(false);
    }
  };

  const resetUploader = () => {
    setFile(null);
    setResult(null);
    setError(null);
    document.getElementById('properCsvFileInput').value = '';
  };

  return (
    <div className="proper-csv-uploader">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Import Vehicles from Proper CSV</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select CSV File
          </label>
          <input
            id="properCsvFileInput"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {file && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
            <p className="text-sm text-green-800">
              Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex gap-3 mb-6">
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className={`px-4 py-2 rounded font-medium ${
              !file || uploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {uploading ? 'Importing...' : 'Import CSV'}
          </button>
          
          <button
            onClick={resetUploader}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
        </div>

        {result && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
            <h3 className="font-semibold text-blue-900 mb-2">Import Complete!</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-green-700">Imported:</span>
                <span className="ml-1 text-green-800">{result.summary.imported}</span>
              </div>
              <div>
                <span className="font-medium text-yellow-700">Skipped:</span>
                <span className="ml-1 text-yellow-800">{result.summary.skipped}</span>
              </div>
              <div>
                <span className="font-medium text-red-700">Errors:</span>
                <span className="ml-1 text-red-800">{result.summary.errors}</span>
              </div>
              <div>
                <span className="font-medium text-blue-700">Total:</span>
                <span className="ml-1 text-blue-800">{result.summary.total}</span>
              </div>
            </div>
            
            {result.results && result.results.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">Sample Results:</h4>
                <div className="max-h-40 overflow-y-auto">
                  {result.results.slice(0, 10).map((item, index) => (
                    <div key={index} className="text-xs text-gray-600 py-1">
                      {item.status === 'imported' && (
                        <span className="text-green-600">✓ {item.year} {item.make} {item.model} (VIN: {item.vin}) → Stock: {item.stockNumber}</span>
                      )}
                      {item.status === 'skipped' && (
                        <span className="text-yellow-600">⚠ {item.vin} ({item.reason})</span>
                      )}
                      {item.status === 'error' && (
                        <span className="text-red-600">✗ {item.vin} ({item.error})</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-sm text-gray-600">
          <h4 className="font-medium mb-2">Supported CSV Columns:</h4>
          <div className="grid grid-cols-2 gap-2">
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Stock Number</strong> - Vehicle stock number</li>
              <li><strong>VIN</strong> - Vehicle identification number (required)</li>
              <li><strong>Year</strong> - Vehicle year</li>
              <li><strong>Make</strong> - Vehicle manufacturer</li>
              <li><strong>Model</strong> - Vehicle model</li>
              <li><strong>In Date</strong> - Purchase/acquisition date</li>
              <li><strong>Status</strong> - Vehicle status (available, sold, etc.)</li>
              <li><strong>Sold</strong> - Whether vehicle is sold (true/false)</li>
              <li><strong>Miles</strong> - Vehicle mileage</li>
              <li><strong>Color</strong> - Vehicle color</li>
            </ul>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Age (days)</strong> - Days in inventory</li>
              <li><strong>Purchase Price</strong> - Amount paid for vehicle</li>
              <li><strong>Recon Cost</strong> - Reconditioning expenses</li>
              <li><strong>Transport Cost</strong> - Transportation expenses</li>
              <li><strong>Other Costs</strong> - Additional costs</li>
              <li><strong>Total Cost</strong> - Total investment</li>
              <li><strong>Selling Price</strong> - Expected/actual sale price</li>
              <li><strong>Auction</strong> - Source auction/dealer</li>
              <li><strong>Vehicle Description</strong> - Additional notes</li>
            </ul>
          </div>
          <div className="mt-3 p-3 bg-gray-50 rounded">
            <p className="text-xs"><strong>Notes:</strong></p>
            <ul className="text-xs mt-1 space-y-1">
              <li>• Column names are case-insensitive and flexible</li>
              <li>• VIN is required - rows without VIN will be skipped</li>
              <li>• Duplicate VINs will be skipped</li>
              <li>• Stock numbers will be auto-generated if not provided</li>
              <li>• Currency values can include $ and commas</li>
              <li>• Dates can be in various formats (MM/DD/YYYY, etc.)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProperCSVUploader;
